/**
 * JourneyView — Customer Journey Timeline + Graph + Messages page.
 * Three projections of the same canonical journey.
 *
 * Route: /journeys/:journeyId
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Clock, GitBranch, ExternalLink, MessageCircle } from "lucide-react";
import { JourneyTimeline } from "@/components/JourneyTimeline";
import { JourneyGraph } from "@/components/JourneyGraph";
import { JourneyMessages } from "@/components/JourneyMessages";

const API = process.env.REACT_APP_BACKEND_URL;

export default function JourneyView() {
  const { journeyId } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState("timeline"); // "timeline" | "graph" | "messages"
  const [meta, setMeta] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [graph, setGraph] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [error, setError] = useState("");

  // Load journey meta
  useEffect(() => {
    if (!journeyId) return;
    fetch(`${API}/api/journeys/${journeyId}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setMeta(d))
      .catch(() => {});
  }, [journeyId]);

  // Load timeline data
  useEffect(() => {
    if (!journeyId || view !== "timeline") return;
    setLoadingTimeline(true);
    fetch(`${API}/api/journeys/${journeyId}/timeline?limit=200`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (d) setTimeline(d.activities || []);
        setLoadingTimeline(false);
      })
      .catch(() => setLoadingTimeline(false));
  }, [journeyId, view]);

  // Load graph data (lazy — only when graph tab selected)
  useEffect(() => {
    if (!journeyId || view !== "graph") return;
    if (graph) return; // already loaded
    setLoadingGraph(true);
    fetch(`${API}/api/journeys/${journeyId}/graph`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (d) setGraph(d);
        setLoadingGraph(false);
      })
      .catch(() => setLoadingGraph(false));
  }, [journeyId, view]);

  // Load AI suggestions (B6/B7) — refresh on timeline/messages tab
  const loadSuggestions = () => {
    if (!journeyId) return;
    fetch(`${API}/api/journeys/${journeyId}/suggestions?status=PENDING`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setSuggestions(d.suggestions || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (view === "timeline" || view === "messages") {
      loadSuggestions();
    }
  }, [journeyId, view]); // eslint-disable-line

  const handleSuggestionAction = async (suggestionId, action, suggestion) => {
    const token = localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    try {
      const r = await fetch(
        `${API}/api/journeys/${journeyId}/suggestions/${suggestionId}/${action}`,
        { method: "POST", headers, credentials: "include" }
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        console.error("Suggestion action failed:", err.detail || action);
        return;
      }
      // Remove from list after successful action
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestionId));
    } catch (e) {
      console.error("Suggestion action error:", e);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="journey-view">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-[#E5E5E5] px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded hover:bg-[#F3F3F3] transition-colors"
            data-testid="journey-back-btn"
          >
            <ArrowLeft className="w-4 h-4 text-[#706E6B]" />
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-base font-bold text-[#181818] truncate" data-testid="journey-title">
                {meta?.customer_name || "Customer Journey"}
              </h1>
              {meta?.lead_number && (
                <span className="text-xs text-[#706E6B] font-mono bg-[#F3F3F3] px-1.5 py-0.5 rounded">
                  {meta.lead_number}
                </span>
              )}
              {meta?.current_stage && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#EEF4FF] text-[#0176D3]">
                  {meta.current_stage.replace(/_/g, " ")}
                </span>
              )}
            </div>
            {meta && (
              <div className="flex items-center gap-3 mt-0.5 text-xs text-[#706E6B]">
                {meta.linked_event_number && (
                  <span>Event: {meta.linked_event_number}</span>
                )}
                {meta.journey_id && (
                  <span className="font-mono">{meta.journey_id}</span>
                )}
              </div>
            )}
          </div>

          {/* Quick links — hidden on mobile to save header space */}
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
            {meta?.lead_id && (
              <Link
                to={`/engage/leads/${meta.lead_id}`}
                className="flex items-center gap-1 text-xs text-[#0176D3] hover:underline"
                data-testid="journey-lead-link"
              >
                Lead <ExternalLink className="w-3 h-3" />
              </Link>
            )}
            {meta?.linked_event_id && (
              <Link
                to={`/events/${meta.linked_event_id}/threads`}
                className="flex items-center gap-1 text-xs text-[#0176D3] hover:underline"
                data-testid="journey-event-link"
              >
                Event <ExternalLink className="w-3 h-3" />
              </Link>
            )}
            {meta?.linked_event_id && (
              <Link
                to={`/events/${meta.linked_event_id}/360`}
                className="flex items-center gap-1 text-xs text-[#0176D3] hover:underline"
              >
                360 <ExternalLink className="w-3 h-3" />
              </Link>
            )}
          </div>
        </div>

        {/* View toggle — scrollable on mobile */}
        <div className="overflow-x-auto pb-1 -mb-px">
        <div className="flex gap-1 mt-3 min-w-max" role="tablist">
          <button
            role="tab"
            data-testid="journey-tab-timeline"
            aria-selected={view === "timeline"}
            onClick={() => setView("timeline")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              view === "timeline"
                ? "bg-[#0176D3] text-white"
                : "text-[#444444] hover:bg-[#F3F3F3]"
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            Timeline
          </button>
          <button
            role="tab"
            data-testid="journey-tab-messages"
            aria-selected={view === "messages"}
            onClick={() => setView("messages")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              view === "messages"
                ? "bg-[#0176D3] text-white"
                : "text-[#444444] hover:bg-[#F3F3F3]"
            }`}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            Messages
          </button>
          <button
            role="tab"
            data-testid="journey-tab-graph"
            aria-selected={view === "graph"}
            onClick={() => setView("graph")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              view === "graph"
                ? "bg-[#0176D3] text-white"
                : "text-[#444444] hover:bg-[#F3F3F3]"
            }`}
          >
            <GitBranch className="w-3.5 h-3.5" />
            Journey Graph
            {graph && (
              <span className="ml-1 text-[10px] opacity-75">{graph.nodes?.length} nodes</span>
            )}
          </button>
        </div>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden">
        {view === "timeline" ? (
          <JourneyTimeline
            activities={timeline}
            loading={loadingTimeline}
            onNavigate={(route) => navigate(route)}
            suggestions={suggestions}
            onSuggestionAction={handleSuggestionAction}
          />
        ) : view === "messages" ? (
          <JourneyMessages journeyId={journeyId} />
        ) : (
          <JourneyGraph
            graphData={graph}
            loading={loadingGraph}
            onNavigate={(route) => navigate(route)}
          />
        )}
      </div>
    </div>
  );
}
